import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOpenTabs } from '../context/OpenTabsContext';
import { setShortcutDragPayload } from '../utils/shortcutDnd';

interface TopNavLinkProps {
  to: string;
  label: string;
  moduleKey?: string;
  state?: any;
  isActive: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Drop-in replacement for the leaf <Link> inside top-nav dropdowns.
 * Adds drag support so a menu entry can be dragged onto the bookmarks
 * bar (ShortcutNavBar) to save it as a shortcut — mirrors the old
 * sidebar's drag-to-bookmark behavior.
 */
const TopNavLink: React.FC<TopNavLinkProps> = ({
  to,
  label,
  moduleKey,
  state,
  onClick,
  className,
  style,
}) => {
  const navigate = useNavigate();
  const { openOrActivateTab } = useOpenTabs();

  const handleDragStart = (e: React.DragEvent<HTMLAnchorElement>) => {
    setShortcutDragPayload(e.dataTransfer, { path: to, label, moduleKey, state });
    // Set text/plain AFTER our MIME so the browser doesn't attach a
    // text/uri-list a drop target might misread as a navigation. Do NOT
    // call preventDefault() here — that would cancel the drag itself.
    e.dataTransfer.setData('text/plain', label);
  };

  return (
    <Link
      to={to}
      state={state}
      draggable
      onDragStart={handleDragStart}
      onClick={(event) => {
        onClick?.(event);

        // The top-menu closes itself in the same click. Navigate explicitly for a
        // normal left click, so closing that menu cannot leave the current
        // keep-alive route active. Modified clicks retain normal browser Link
        // behaviour (new tab/window, context menu, etc.).
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.altKey ||
          event.ctrlKey ||
          event.shiftKey
        ) {
          return;
        }
        event.preventDefault();
        // KeepAliveOutlet renders from the open-tab registry. Register the
        // destination in this same click, rather than waiting for its
        // post-navigation effect, so the old screen cannot remain visible
        // while the URL has already changed.
        openOrActivateTab(to, label, state);
        navigate(to, { state });
      }}
      title={label}
      className={`${className ?? ''} cursor-grab active:cursor-grabbing`}
      style={style}
    >
      {label}
    </Link>
  );
};

export default TopNavLink;
